import type { Fuel, Gearbox, BodyType } from './car';
import type { Currency, ListingAvailability } from './listing';

/**
 * Frontend mirror of the storefront assistant's api (api#381, api#425): `GET /buyer/assistant/status`
 * and `POST /buyer/assistant/messages`. Source of truth: api `src/infrastructure/dto/assistant/*`.
 */

/** The catalog the person talks from: its rules and functions on the api; a conversation stays on it. */
export type AssistantSurface = 'rent' | 'buy';

export interface ApiAssistantStatus {
  /** The installation has a model: the panel shows its entry. */
  enabled: boolean;
  /**
   * A visitor who has not signed in may ask too, within the anonymous daily limits (api#441). Absent
   * on an api before it: the panel then asks a visitor to sign in first, as it always did.
   */
  openToAnonymous?: boolean;
}

/**
 * Whose share of the day a refused message spent (`payload.scope` of ASSISTANT_DAILY_LIMIT): past a
 * visitor's own or all visitors', signing in is the way on (api#441).
 */
export type AssistantDailyLimitScope = 'account' | 'visitor' | 'anonymous' | 'platform';

export interface ApiAssistantMessagePayload {
  /** A conversation of this party (the account, or the anonymous visitor) to continue, from an earlier reply; omitted = a new one. */
  conversationId?: string;
  /** The catalog the person is on; the api takes an omitted one as `rent`. */
  surface?: AssistantSurface;
  text: string;
}

/** A car for rent as the assistant's reply shows it: a snapshot taken from the function result the model read. */
export interface ApiAssistantRentalCar {
  vertical: 'rent';
  listingId: string;
  /** "Make Model Year", as the catalog card titles it. */
  title: string;
  year: number | null;
  /** The short-term ("from") daily price, in the listing's currency. */
  dailyPrice: number | null;
  currency: Currency | null;
  depositAmount: number | null;
  location: string | null;
  /** The first photo's storage key (`formatImageUrl`), or none. */
  image: string | null;
  seats: number | null;
  dailyKmLimit: number | null;
  dailyKmUnlimited: boolean;
  /**
   * The dealer's daily price of each band (app#362): with no dates the card shows the catalogue
   * card's price by the catalogue's rule. Absent from an api before it.
   */
  tierPrices?: ApiAssistantTierPrices;
  /** The price of the booking for the dates of the reply (app#362). Absent without dates, and from an api before it. */
  booking?: ApiAssistantRentalBooking;
  /** The Trip cost line (api#430), for the dates of the reply. Absent from an api before it, and before the trip was counted. */
  trip?: ApiAssistantTripCost | null;
}

/** The dealer's grid, band by band: 1–3, 4–7, 8–15 and 16+ days. */
export interface ApiAssistantTierPrices {
  tier1DailyPrice: number | null;
  tier2DailyPrice: number | null;
  tier3DailyPrice: number | null;
  tier4DailyPrice: number | null;
}

/** One of the platform's price bands and the dealer's daily price for it. */
export interface ApiAssistantRateBand {
  minDays: number;
  /** Null on the open-ended top band (16+ days). */
  maxDays: number | null;
  dailyPrice: number;
}

/** A booking as the platform quoted it: its dates, its band, and what the band saves against 1–3 days. */
export interface ApiAssistantRentalBooking {
  /** `YYYY-MM-DD`: the dates quoted. */
  startDate: string;
  endDate: string;
  days: number;
  /** The band the booking falls into: its daily price is the quote's. */
  rate: ApiAssistantRateBand;
  /** Null for a 1–3 day booking and for a band no cheaper than it. */
  saving: { against: ApiAssistantRateBand; perDay: number; total: number } | null;
}

/**
 * What the fuel of a trip came to (api#430): `counted` — official consumption times the
 * kilometres; `km_not_stated` — the person named no kilometres a day; `fuel_not_stated` — no fuel
 * in the listing nor its description; `not_integrated` — the official data rates no such engine;
 * `unavailable` — the data could not be read right now.
 */
export type AssistantTripFuelStatus = 'counted' | 'km_not_stated' | 'fuel_not_stated' | 'not_integrated' | 'unavailable';

/** What the price of that fuel came to: the EU's bulletin of the pickup country, or why there is none. */
export type AssistantTripFuelPriceStatus = 'counted' | 'not_in_bulletin' | 'place_unknown' | 'electric' | 'unavailable';

export interface ApiAssistantRange {
  min: number;
  max: number;
}

export interface ApiAssistantTripCost {
  /** `YYYY-MM-DD`: the dates the trip was counted for. */
  startDate: string;
  endDate: string;
  days: number;
  kmPerDay: number | null;
  km: number | null;
  /** The rental's currency: the quote's numbers are in it. */
  currency: Currency;
  rental: number;
  platformFee: number;
  /** Comes back after the rental. */
  deposit: number;
  /** What the booking takes now: the rental, the fee and the deposit. */
  payNow: number;
  /** The car's daily kilometre limit, when the person's day is over it. */
  overKmLimit: number | null;
  fuel: {
    status: AssistantTripFuelStatus;
    fuel: Fuel | null;
    fuelFrom: 'listing' | 'description' | null;
    engineLitres: number | null;
    per100Km: ApiAssistantRange | null;
    unit: 'l' | 'kWh' | null;
    amount: ApiAssistantRange | null;
    /** A plug-in hybrid counted on petrol alone. */
    petrolOnly: boolean;
    source: 'us_epa' | null;
    price: {
      status: AssistantTripFuelPriceStatus;
      country: { code: string; name: string } | null;
      /** Euro per litre with taxes. */
      perLitre: number | null;
      /** `YYYY-MM-DD`: the bulletin's day. */
      date: string | null;
      /** Whole euros for the trip: the bulletin's currency, never converted. */
      cost: ApiAssistantRange | null;
    } | null;
  };
}

/** Whether the listing states a VIN, and whether it has the form of one. */
export type AssistantVinStatus = 'stated' | 'not_stated' | 'malformed';

/**
 * The VIN against the listing: `matches` — the decoded make (and year, read cleanly) agree;
 * `differs` — see `differences`; `unknown` — the decoder has no record of it; `unavailable` — the
 * decoder could not be reached; `unchecked` — nothing to decode.
 */
export type AssistantVinCheck = 'matches' | 'differs' | 'unknown' | 'unavailable' | 'unchecked';

export interface ApiAssistantVinEvidence {
  status: AssistantVinStatus;
  check: AssistantVinCheck;
  decoded: {
    make: string | null;
    manufacturer: string | null;
    model: string | null;
    year: number | null;
    bodyClass: string | null;
    fuel: string | null;
    /** The country the car was built in. */
    builtIn: string | null;
  } | null;
  differences: ('make' | 'year')[];
  /** The model year the VIN itself gives, when it confirms the listing's (api#440). Absent from an api before it. */
  modelYear?: number | null;
  /** The VIN's first character is 1–5: built in North America. */
  builtInNorthAmerica?: boolean;
}

/** One check of a car on someone else's site (api#430): the person goes there. */
export interface ApiAssistantCarCheck {
  id: string;
  name: string;
  url: string;
  /** What it tells. */
  about: string;
  /** What the person enters there. */
  needs: string;
  cost: 'free' | 'paid';
}

/** The "Check this car" list under a car for sale (api#430): links only; the platform reads no report. */
export interface ApiAssistantCarChecks {
  /** Null: the dealer has not stated a VIN of a VIN's form — the list is one line. */
  vin: string | null;
  dealerCountry: { code: string; name: string } | null;
  countryChecksKnown: boolean;
  official: ApiAssistantCarCheck[];
  /** A sentence about a country with no check to link (Germany). */
  countryNote: string | null;
  builtInNorthAmerica: boolean;
  us: ApiAssistantCarCheck[];
  searchUrl: string | null;
  reports: { provider: string; url: string }[];
}

/** The price with the platform fee of the escrow with delivery (api#430); the dealer quotes the delivery. */
export interface ApiAssistantDeliveredPrice {
  price: number;
  currency: Currency;
  platformFee: number;
  feePercent: number;
  total: number;
}

export type AssistantPricePosition =
  | 'only_one'
  | 'lowest'
  | 'below_median'
  | 'at_median'
  | 'above_median'
  | 'highest';

/**
 * The dealer's record on this platform (api#434): counted by the platform, the delivery deals that
 * did not go through grouped by who stopped them. No rating.
 */
export interface ApiAssistantDealerEvidence {
  /** `YYYY-MM`: the month the dealer joined. */
  since: string | null;
  /** The cars the dealer's page shows for sale. */
  carsForSale: number;
  carsForRent: number;
  delivery: {
    handedOver: number;
    inProgress: number;
    inDispute: number;
    withdrawnByBuyer: number;
    cancelledByBuyer: number;
    /** The dealer approved, the buyer did not pay in time. */
    notPaidInTime: number;
    declinedByDealer: number;
    /** The dealer did not answer in time, or the car went to another buyer. */
    offersExpired: number;
    refundedAfterDispute: number;
  };
  buyNow: { paid: number; beingVerified: number; notConfirmed: number };
  rental: { completed: number; inProgress: number; inDispute: number; cancelledByRenter: number };
}

/** The evidence card of a car for sale (api#425): every number computed by the platform from the catalogue. */
export interface ApiAssistantSaleEvidence {
  listed: {
    year: number | null;
    mileage: number | null;
    bodyType: BodyType | null;
    gearbox: Gearbox | null;
    fuel: Fuel | null;
    color: string | null;
    photos: number;
    descriptionStated: boolean;
  };
  vin: ApiAssistantVinEvidence;
  price: {
    price: number;
    currency: Currency;
    /** Similar cars of this catalogue besides this one (same make and model, years within 2, same currency), and their dealers. */
    similar: number;
    dealers: number;
    min: number | null;
    median: number | null;
    max: number | null;
    position: AssistantPricePosition;
    /** Rounded percent from the median; negative is cheaper. */
    fromMedianPercent: number | null;
    mileageOfSimilar: { min: number; max: number } | null;
  };
  purchase: {
    availability: ListingAvailability;
    /** Available, and the dealer holds a payout wallet for its token. */
    buyable: boolean;
    tokenSymbol: string | null;
    chainKey: string | null;
  };
  /** Null: the record could not be read. Absent from an api before api#434, and on a card computed before it. */
  dealer?: ApiAssistantDealerEvidence | null;
  /** Null: the car cannot be bought now. Absent from an api before api#430. */
  delivered?: ApiAssistantDeliveredPrice | null;
  /** Absent from an api before api#430. */
  checks?: ApiAssistantCarChecks;
}

/** A car for sale as the assistant's reply shows it: the card and its evidence (always set on a car the reply shows). */
export interface ApiAssistantSaleCar {
  vertical: 'buy';
  listingId: string;
  title: string;
  year: number | null;
  price: number;
  currency: Currency;
  mileage: number | null;
  image: string | null;
  /** The dealer: the link to `/dealer/:sellerId`. */
  sellerId: string;
  evidence: ApiAssistantSaleEvidence | null;
}

export type ApiAssistantCar = ApiAssistantRentalCar | ApiAssistantSaleCar;

export interface ApiAssistantPickedCar {
  listingId: string;
  /** One or two sentences: why this car, from its data and its evidence. */
  reason: string;
  /** A car for sale's generation, general knowledge (api#430): shown labelled "General information". */
  generation?: string;
  car: ApiAssistantCar;
}

export interface ApiAssistantDates {
  /** `YYYY-MM-DD` */
  startDate: string;
  endDate: string;
}

/**
 * What the panel does with the reply: `book` — the cars can be booked on these dates ("Book these
 * dates" on every card); `prepareOffer` — cars for sale, "Prepare the offer" on every card opens
 * the car's page with the delivery offer started, the city filled in when the person named one;
 * `ask` — the assistant needs what `missing` names; `none` — a plain answer.
 */
export type ApiAssistantSuggestedAction =
  | { type: 'book'; dates: ApiAssistantDates }
  | { type: 'prepareOffer'; deliveryPointId: string | null; deliveryCity: string | null }
  | { type: 'ask'; missing: string[] }
  | { type: 'none' };

export interface ApiAssistantReply {
  /** Sent back with the next message to continue the conversation. */
  conversationId: string;
  /** The assistant's message, in the person's language. */
  text: string;
  /** Up to three cars, best fit first; empty for a question or when nothing fits. */
  cars: ApiAssistantPickedCar[];
  suggestedAction: ApiAssistantSuggestedAction;
  /** How many more messages this conversation takes today before a new one, or tomorrow, is needed. */
  remainingMessages: number;
}

/** The api's codes on a refused message (`RequestFailure.code`). */
export const ASSISTANT_CODES = {
  /** No model on this installation. */
  disabled: 'ASSISTANT_DISABLED',
  /** The model did not answer; try again in a moment. */
  modelUnavailable: 'ASSISTANT_MODEL_UNAVAILABLE',
  /** The conversation is not open any more: start a new one. */
  conversationNotFound: 'ASSISTANT_CONVERSATION_NOT_FOUND',
  /** The conversation took every message it may: start a new one. */
  conversationLimit: 'ASSISTANT_CONVERSATION_LIMIT',
  /** Another message of this conversation is being answered. */
  conversationBusy: 'ASSISTANT_CONVERSATION_BUSY',
  /** The day's messages are spent: the account's, a visitor's, all visitors' or the platform's (`payload.scope`). */
  dailyLimit: 'ASSISTANT_DAILY_LIMIT',
} as const;
